import React from "react";
import { useLang } from "../i18n/LanguageContext";
import Reveal from "./Reveal";
import { Users } from "lucide-react";

const HEADING = {
  eyebrow: { id: "Armada Kami", en: "Our Fleet", zh: "我们的车队", ja: "車両ラインナップ", ko: "차량 라인업", vi: "Đội xe của chúng tôi", th: "ฟลีตรถของเรา" },
  title: { id: "Armada premium ber-AC, siap antar Anda ke setiap sudut Batam.", en: "Premium air-conditioned fleet — comfort for every Batam journey.", zh: "舒适冷气车队，带您游遍巴淡每个角落。", ja: "エアコン完備のプレミアム車両でバタムを快適に。", ko: "에어컨 완비 프리미엄 차량으로 바탐 어디든 편안하게.", vi: "Đội xe cao cấp có máy lạnh — đồng hành mọi hành trình tại Batam.", th: "ฟลีตรถพรีเมียมติดแอร์ พร้อมพาคุณเที่ยวทุกมุมของบาตัม" },
};

const FLEET = [
  {
    img: "/fleet/bus.jpg",
    name: { id: "Big Bus 27-45 Pax", en: "Big Bus · 27-45 Pax", zh: "大巴 · 27-45 人", ja: "大型バス · 27-45名", ko: "대형 버스 · 27-45명", vi: "Xe khách lớn · 27-45 chỗ", th: "รถบัส · 27-45 ที่นั่ง" },
    desc: { id: "Untuk rombongan keluarga besar, korporat, atau tur group.", en: "For large family groups, corporates, and group tours.", zh: "适合大型家庭、企业团或团体游。", ja: "大人数の家族、団体・企業ツアーに最適。", ko: "대가족, 기업, 단체 투어에 적합.", vi: "Phù hợp đoàn lớn, doanh nghiệp và tour theo nhóm.", th: "เหมาะกับครอบครัวใหญ่ กรุ๊ปบริษัท หรือทัวร์หมู่คณะ" },
    featured: true,
  },
  {
    img: "/fleet/hiace.jpg",
    name: { id: "Toyota Hiace 13 Pax", en: "Toyota Hiace · 13 Pax", zh: "丰田海狮 · 13 人", ja: "トヨタ ハイエース · 13名", ko: "토요타 하이에이스 · 13명", vi: "Toyota Hiace · 13 chỗ", th: "Toyota Hiace · 13 ที่นั่ง" },
    desc: { id: "Pilihan ideal untuk grup menengah & family trip nyaman.", en: "Ideal for mid-size groups and comfortable family trips.", zh: "中型团体与家庭出游的理想之选。", ja: "中規模グループや家族旅行に最適。", ko: "중간 규모 그룹과 편안한 가족 여행에 이상적.", vi: "Lựa chọn lý tưởng cho nhóm vừa và gia đình.", th: "ตัวเลือกที่ดีสำหรับกลุ่มขนาดกลางและทริปครอบครัว" },
  },
  {
    img: "/fleet/innova.jpg",
    name: { id: "Toyota Innova 6 Pax", en: "Toyota Innova · 6 Pax", zh: "丰田 Innova · 6 人", ja: "トヨタ イノーバ · 6名", ko: "토요타 이노바 · 6명", vi: "Toyota Innova · 6 chỗ", th: "Toyota Innova · 6 ที่นั่ง" },
    desc: { id: "Cocok untuk private trip, couple, atau keluarga kecil.", en: "Perfect for private trips, couples, and small families.", zh: "适合私享游、情侣或小家庭出行。", ja: "プライベートツアー、カップル、少人数家族に最適。", ko: "프라이빗 투어, 커플, 소규모 가족에 적합.", vi: "Hoàn hảo cho tour riêng, cặp đôi và gia đình nhỏ.", th: "เหมาะกับทริปส่วนตัว คู่รัก หรือครอบครัวเล็ก" },
  },
];

const Card = ({ vehicle, lang, featured }) => (
  <article
    data-testid={`armada-card-${vehicle.name.en.split("·")[0].trim().toLowerCase().replace(/\s+/g, "-")}`}
    className={`group relative overflow-hidden rounded-3xl ring-1 ring-white/10 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.6)] ${
      featured ? "md:col-span-2 h-[300px] md:h-[400px]" : "h-[260px] md:h-[300px]"
    }`}
  >
    <img
      src={vehicle.img}
      alt={vehicle.name.en}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0C2230]/95 via-[#0C2230]/30 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-md ring-1 ring-white/20">
        <Users className="h-3 w-3" strokeWidth={2.2} />
        <span>{vehicle.name[lang] || vehicle.name.en}</span>
      </div>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-white/85 md:text-base">
        {vehicle.desc[lang] || vehicle.desc.en}
      </p>
    </div>
  </article>
);

export const Armada = () => {
  const { lang } = useLang();
  return (
    <section
      id="armada"
      data-testid="armada-section"
      className="relative overflow-hidden bg-[#0C2230] py-20 text-white md:py-24"
    >
      <div className="pointer-events-none absolute -top-32 right-0 h-96 w-96 rounded-full bg-[#0E9A57]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 left-0 h-[28rem] w-[28rem] rounded-full bg-[#1E7FC2]/10 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <Reveal>
          <div className="grid items-end gap-6 md:grid-cols-12">
            <div className="md:col-span-8">
              <span className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#B7892F]">
                <span className="h-px w-8 bg-[#B7892F]" />
                {HEADING.eyebrow[lang] || HEADING.eyebrow.en}
              </span>
              <h2 className="mt-5 font-[Fraunces] text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl">
                {HEADING.title[lang] || HEADING.title.en}
              </h2>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {FLEET.map((vehicle) => (
            <Reveal
              key={vehicle.name.en}
              delay={0.05}
              className={vehicle.featured ? "md:col-span-2" : ""}
            >
              <Card vehicle={vehicle} lang={lang} featured={vehicle.featured} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Armada;
